import React, { useEffect, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef } from '@backstage/core-plugin-api';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  makeStyles,
  Link,
  CircularProgress,
  Chip,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  TablePagination,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import ErrorIcon from '@material-ui/icons/Error';

const useStyles = makeStyles(theme => ({
  card: {
    marginBottom: theme.spacing(2),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  issueItem: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  issueTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
  issueHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
  },
  issueMeta: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  issueDate: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  chip: {
    height: 20,
  },
  statCard: {
    padding: theme.spacing(2),
    textAlign: 'center',
    background: theme.palette.type === 'dark' 
      ? 'linear-gradient(135deg, rgba(66, 66, 66, 0.5) 0%, rgba(33, 33, 33, 0.5) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 242, 245, 0.9) 100%)',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
}));

interface SentryIssue {
  id: string;
  title: string;
  culprit: string;
  permalink: string;
  level: string;
  count: string;
  userCount: number;
  status: string;
  firstSeen: string;
  lastSeen: string;
  stats?: {
    [key: string]: Array<[number, number]>;
  };
}

interface ProjectStats {
  crashFreeRate: number;
  crashFreeUsers: number;
  totalIssues: number;
  totalEvents: number;
}

type TimePeriod = '1h' | '24h' | '7d' | '14d' | '30d' | 'all';

/**
 * Custom Sentry card that fetches and displays issues
 * Reads configuration from entity annotations:
 * - sentry.io/organization
 * - sentry.io/project-slug
 * - sentry.io/api-url (optional, defaults to https://sentry.io/api/0)
 * - sentry.io/token (optional)
 */
export const SentryCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const [allIssues, setAllIssues] = useState<SentryIssue[]>([]);
  const [stats, setStats] = useState<ProjectStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('24h');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const annotations = entity.metadata.annotations || {};
  const organization = annotations['sentry.io/organization'];
  const projectSlug = annotations['sentry.io/project-slug'];
  const apiUrl = annotations['sentry.io/api-url'] || 'https://sentry.io/api/0';
  const token = annotations['sentry.io/token'];

  // For self-hosted Sentry, remove /0 version suffix if present
  const baseApiUrl = apiUrl.replace('/api/0', '/api');

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getPeriodLabel = (period: TimePeriod) => {
    switch (period) {
      case '1h': return 'Last Hour';
      case '24h': return 'Last 24 Hours';
      case '7d': return 'Last 7 Days';
      case '14d': return 'Last 14 Days';
      case '30d': return 'Last 30 Days';
      case 'all': return 'All Time';
      default: return 'Last 24 Hours';
    }
  };

  const getPeriodKey = (period: TimePeriod): string => {
    if (period === '1h') return '24h'; // Sentry returns 1h data in 24h stats
    if (period === '7d') return '7d';
    if (period === '14d') return '14d';
    if (period === '30d') return '30d';
    return '24h';
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const formatPercentage = (value: number): string => {
    return value === 0 ? '0%' : `${value.toFixed(3)}%`;
  };

  // Paginate issues
  const paginatedIssues = allIssues.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!organization || !projectSlug) {
        setError('Missing required annotations: sentry.io/organization and sentry.io/project-slug');
        setLoading(false);
        return;
      }

      setLoading(true);
      setPage(0);

      try {
        // Get backend URL dynamically (using plugin ID 'sentry-proxy')
        const backendUrl = await discoveryApi.getBaseUrl('sentry-proxy');
        
        // Fetch issues
        const issuesPath = `organizations/${organization}/issues`;
        const issuesParams = new URLSearchParams({
          apiUrl: baseApiUrl,
          project: projectSlug,
        });
        
        if (timePeriod && timePeriod !== 'all') {
          issuesParams.append('statsPeriod', timePeriod);
        }
        
        if (token) {
          issuesParams.append('token', token);
        }
        
        const issuesUrl = `${backendUrl}/${issuesPath}?${issuesParams.toString()}`;
        
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };

        const issuesResponse = await fetch(issuesUrl, { headers });

        if (!issuesResponse.ok) {
          const errorText = await issuesResponse.text();
          console.error('Sentry API error:', { 
            status: issuesResponse.status, 
            statusText: issuesResponse.statusText,
            body: errorText 
          });
          throw new Error(`Sentry API error: ${issuesResponse.status} - ${errorText.substring(0, 200)}`);
        }

        const issuesData = await issuesResponse.json();
        
        // Filter unresolved issues
        const unresolvedIssues = Array.isArray(issuesData) 
          ? issuesData.filter((issue: SentryIssue) => issue.status === 'unresolved')
          : [];
        
        setAllIssues(unresolvedIssues);

        // Calculate total events from stats in each issue
        let totalEventsFromStats = 0;
        
        if (timePeriod === 'all') {
          // For "All Time", use lifetime count
          unresolvedIssues.forEach((issue: SentryIssue) => {
            if (issue.count) {
              totalEventsFromStats += parseInt(issue.count, 10);
            }
          });
        } else {
          const periodKey = getPeriodKey(timePeriod || '24h');
          unresolvedIssues.forEach((issue: SentryIssue) => {
            // Sum events from stats for the period
            if (issue.stats && issue.stats[periodKey]) {
              const periodStats = issue.stats[periodKey];
              const eventsInPeriod = periodStats.reduce((sum: number, stat: [number, number]) => sum + stat[1], 0);
              totalEventsFromStats += eventsInPeriod;
            } else if (issue.count) {
              // Fallback to count if stats not available
              totalEventsFromStats += parseInt(issue.count, 10);
            }
          });
        }

        // Fetch real session stats from Sentry
        try {
          const sessionsPath = `organizations/${organization}/sessions`;
          const sessionsParams = new URLSearchParams();
          
          sessionsParams.append('apiUrl', baseApiUrl);
          sessionsParams.append('project', projectSlug);
          sessionsParams.append('field', 'crash_free_rate(session)');
          sessionsParams.append('field', 'crash_free_rate(user)');
          
          if (timePeriod && timePeriod !== 'all') {
            sessionsParams.append('statsPeriod', timePeriod);
            
            // Add interval to match Sentry UI calculation
            if (timePeriod === '1h' || timePeriod === '24h') {
              sessionsParams.append('interval', '1h');
            } else {
              sessionsParams.append('interval', '1d');
            }
          }
          
          if (token) {
            sessionsParams.append('token', token);
          }
          
          const sessionsUrl = `${backendUrl}/${sessionsPath}?${sessionsParams.toString()}`;
          const sessionsResponse = await fetch(sessionsUrl, { headers });

          let crashFreeRate = 100;
          let crashFreeUsers = 0;

          if (sessionsResponse.ok) {
            const sessionsData = await sessionsResponse.json();
            
            if (sessionsData.groups && sessionsData.groups.length > 0) {
              const group = sessionsData.groups[0];
              const totals = group.totals;
              
              // Get crash free rates from Sentry (they're in decimal form 0.0-1.0)
              if (totals['crash_free_rate(session)'] !== null && totals['crash_free_rate(session)'] !== undefined) {
                crashFreeRate = totals['crash_free_rate(session)'] * 100;
              }
              
              if (totals['crash_free_rate(user)'] !== null && totals['crash_free_rate(user)'] !== undefined) {
                crashFreeUsers = totals['crash_free_rate(user)'] * 100;
              } else {
                // If user rate is null (like in Sentry UI showing 0%), set to 0
                crashFreeUsers = 0;
              }
            }
          }

          setStats({
            crashFreeRate: Math.max(0, Math.min(100, crashFreeRate)),
            crashFreeUsers: Math.max(0, Math.min(100, crashFreeUsers)),
            totalIssues: unresolvedIssues.length,
            totalEvents: totalEventsFromStats,
          });
        } catch (sessionsErr) {
          console.warn('Could not fetch session stats:', sessionsErr);
          
          // Fallback if sessions API fails
          setStats({
            crashFreeRate: 100,
            crashFreeUsers: 0,
            totalIssues: unresolvedIssues.length,
            totalEvents: totalEventsFromStats,
          });
        }

        setError(null);
      } catch (err) {
        console.error('Sentry fetch error:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch Sentry data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organization, projectSlug, apiUrl, token, timePeriod]);

  if (!organization || !projectSlug) {
    return (
      <Card className={classes.card}>
        <CardHeader title="Sentry Issues" />
        <CardContent>
          <Alert severity="info">
            Add <code>sentry.io/organization</code> and <code>sentry.io/project-slug</code> annotations to enable Sentry integration
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const sentryBaseUrl = baseApiUrl.replace('/api', '');
  const sentryProjectUrl = `${sentryBaseUrl}/organizations/${organization}/projects/${projectSlug}/`;

  return (
    <Card className={classes.card}>
      <CardHeader 
        title={
          <Box style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span>Sentry Issues</span>
            <Link href={sentryProjectUrl} target="_blank" rel="noopener" style={{ fontSize: '0.875rem' }}>
              View in Sentry →
            </Link>
          </Box>
        }
        action={
          <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={timePeriod}
              onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
              label="Time Period"
            >
              <MenuItem value="1h">Last Hour</MenuItem>
              <MenuItem value="24h">Last 24 Hours</MenuItem>
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="14d">Last 14 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
        }
      />
      <CardContent>
        {/* Statistics Section */}
        {!loading && !error && stats && (
          <Grid container spacing={2} style={{ marginBottom: 24 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper className={classes.statCard}>
                <Typography className={classes.statValue} style={{ marginBottom: 8 }}>
                  {formatPercentage(stats.crashFreeRate)}
                </Typography>
                <Typography className={classes.statLabel}>
                  Crash Free Sessions
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper className={classes.statCard}>
                <Typography className={classes.statValue} style={{ marginBottom: 8 }}>
                  {formatPercentage(stats.crashFreeUsers)}
                </Typography>
                <Typography className={classes.statLabel}>
                  Crash Free Users
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper className={classes.statCard}>
                <Typography className={classes.statValue} style={{ marginBottom: 8 }}>
                  {stats.totalIssues}
                </Typography>
                <Typography className={classes.statLabel}>
                  Unresolved Issues
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper className={classes.statCard}>
                <Typography className={classes.statValue} style={{ marginBottom: 8 }}>
                  {stats.totalEvents}
                </Typography>
                <Typography className={classes.statLabel}>
                  Total Events
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Total Issues Counter */}
        {!loading && !error && (
          <Box style={{ marginBottom: 16, textAlign: 'right' }}>
            <Typography variant="body1" style={{ fontWeight: 600 }}>
              {allIssues.length} total issues
            </Typography>
          </Box>
        )}

        {loading && (
          <Box className={classes.loading}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">
            {error}
            <br />
            <Typography variant="caption">
              If you see CORS error, contact admin to enable CORS in Sentry settings
            </Typography>
          </Alert>
        )}

        {!loading && !error && allIssues.length === 0 && (
          <Alert severity="success">
            No unresolved issues {timePeriod ? `in the ${getPeriodLabel(timePeriod).toLowerCase()}` : ''}! 🎉
          </Alert>
        )}

        {!loading && !error && paginatedIssues.length > 0 && (
          <>
            {paginatedIssues.map(issue => (
              <Box key={issue.id} className={classes.issueItem}>
                <Box className={classes.issueHeader}>
                  <Box style={{ flex: 1 }}>
                    <Link 
                      href={issue.permalink} 
                      target="_blank" 
                      rel="noopener"
                      className={classes.issueTitle}
                    >
                      <Typography variant="body1" component="span">
                        {issue.title}
                      </Typography>
                    </Link>
                    {issue.culprit && (
                      <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
                        {issue.culprit}
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography className={classes.issueDate}>
                      Last seen: {formatDate(issue.lastSeen)}
                    </Typography>
                    <Typography className={classes.issueDate}>
                      First seen: {formatDate(issue.firstSeen)}
                    </Typography>
                  </Box>
                </Box>
                <Box className={classes.issueMeta}>
                  <Chip
                    icon={<ErrorIcon />}
                    label={issue.level}
                    size="small"
                    className={classes.chip}
                    color={issue.level === 'error' ? 'secondary' : 'default'}
                  />
                  <Typography variant="caption" color="textSecondary">
                    {issue.count} events
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {issue.userCount} users
                  </Typography>
                  <Chip
                    label={issue.status}
                    size="small"
                    className={classes.chip}
                  />
                </Box>
              </Box>
            ))}
            
            {/* Pagination */}
            {allIssues.length > 10 && (
              <TablePagination
                component="div"
                count={allIssues.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

